import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, ClipboardList, Plane, ArrowRight, ShieldCheck, Sparkles, Phone } from "lucide-react";
import LeadCaptureForm from "../components/LeadCaptureForm";
import StickyLeadModal from "../components/StickyLeadModal";
import FAQAccordion from "../components/FAQAccordion";
import SmartApartmentIdx from "../components/SmartApartmentIdx";
import { useSEO } from "../lib/useSEO";
import { useJsonLd, buildLocalBusinessSchema, buildBreadcrumbSchema, buildFaqSchema } from "../lib/useJsonLd";
import { NEIGHBORHOODS, relatedNeighborhoods } from "../lib/neighborhoodConfig";
import { articlesForNeighborhood } from "../lib/blogConfig";
import { TYPEFORM_QUIZ_URL } from "../lib/externalLinks";
import { BRAND } from "../lib/brandConfig";

/**
 * /apartments/:slug — Urban Nest Quest neighborhood landing page.
 * One component, config-driven. Adds LocalBusiness + Breadcrumb + FAQPage JSON-LD.
 */
const NeighborhoodPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cfg = NEIGHBORHOODS[slug];

  useEffect(() => {
    if (!cfg) navigate("/apartment-search", { replace: true });
  }, [cfg, navigate]);

  // SEO + JSON-LD MUST be called unconditionally to keep hook order stable.
  // Fall back to safe defaults when cfg is missing (redirect already queued).
  useSEO({
    title: cfg?.seoTitle || "Houston Apartments",
    description: cfg?.metaDescription || "",
    keywords: cfg?.keywords || [],
    path: `/apartments/${slug || ""}`,
  });

  useJsonLd(
    `${slug || "neighborhood"}-local-business`,
    cfg ? buildLocalBusinessSchema(cfg) : null
  );
  useJsonLd(
    `${slug || "neighborhood"}-breadcrumb`,
    cfg
      ? buildBreadcrumbSchema([
          { name: "Home", url: "https://urbannestquest.com/" },
          { name: "Apartment Search", url: "https://urbannestquest.com/apartment-search" },
          { name: cfg.name, url: `https://urbannestquest.com/apartments/${cfg.slug}` },
        ])
      : null
  );
  useJsonLd(
    `${slug || "neighborhood"}-faq`,
    cfg ? buildFaqSchema(cfg.faqs) : null
  );

  if (!cfg) return null;

  const related = relatedNeighborhoods(cfg.slug, 4).map((s) => NEIGHBORHOODS[s]);
  const hoodArticles = articlesForNeighborhood(cfg.slug).slice(0, 6);

  return (
    <div className="bg-[#FAFAF7] pt-20" data-testid={`neighborhood-page-${cfg.slug}`}>
      {/* Breadcrumb */}
      <nav className="container-main pt-6 text-xs text-[#333333]/70" aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="hover:text-[#C8A96B]">Home</Link></li>
          <li>›</li>
          <li><Link to="/apartment-search" className="hover:text-[#C8A96B]">Apartment Search</Link></li>
          <li>›</li>
          <li className="text-[#1F2A44] font-semibold">{cfg.name}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="bg-[#1F2A44] text-white mt-4">
        <div className="container-main py-12 sm:py-16">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                      className="max-w-3xl">
            <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-widest text-[#C8A96B] border border-[#C8A96B]/40 font-semibold">
              <MapPin className="w-3.5 h-3.5" /> {cfg.eyebrow}
            </p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold font-['Playfair_Display'] leading-tight"
                data-testid="neighborhood-h1">
              {cfg.h1}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/80">{cfg.intro}</p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a href={TYPEFORM_QUIZ_URL} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#C8A96B] hover:bg-[#B5915A] text-white font-bold transition-colors"
                 data-testid="hood-hero-quiz">
                <ClipboardList className="w-4 h-4" /> Take the Match Quiz
              </a>
              <a href={BRAND.smsHref}
                 className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-white/30 hover:bg-white hover:text-[#1F2A44] font-semibold transition-colors"
                 data-testid="hood-hero-text">
                <Phone className="w-4 h-4" /> Text us: {BRAND.phone}
              </a>
            </div>

            {cfg.landmarks?.length > 0 && (
              <div className="mt-7">
                <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold mb-2">
                  Nearby landmarks
                </p>
                <div className="flex flex-wrap gap-2">
                  {cfg.landmarks.map((l) => (
                    <span key={l} className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs text-white/85">
                      {l}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Lead form + benefits */}
      <section className="container-main py-10 sm:py-14">
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-10 items-start">
          <div className="lg:col-span-2">
            <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">
              Step 1 — Tell us about you
            </p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-[#1F2A44] font-['Playfair_Display'] leading-tight">
              Get matched in {cfg.name}
            </h2>
            <p className="mt-3 text-sm sm:text-base text-[#333333]/80">
              Skip the bad listings. Submit your details — your {cfg.name} apartment locator will text you the best 3 matches with current specials and tour times.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-[#333333]/85">
              <li className="flex gap-2"><span className="text-[#C8A96B]">✓</span> 100% free service</li>
              <li className="flex gap-2"><span className="text-[#C8A96B]">✓</span> Access to unlisted specials in {cfg.name}</li>
              <li className="flex gap-2"><span className="text-[#C8A96B]">✓</span> Local market expertise — block by block</li>
            </ul>
          </div>
          <div className="lg:col-span-3">
            <LeadCaptureForm source={cfg.leadSource} submitLabel={`View ${cfg.name} Apartments`} />
          </div>
        </div>
      </section>

      {/* Live IDX Inventory Search */}
      <section className="container-main pb-12">
        <SmartApartmentIdx title={`Search Live ${cfg.name} Apartments`} />
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-[#D9D9D9]">
        <div className="container-main py-12 sm:py-16">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">Frequently asked</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
              Renting in {cfg.name}: what to know
            </h2>
          </div>
          <div className="mt-6 max-w-3xl">
            <FAQAccordion faqs={cfg.faqs} idPrefix={`faq-${cfg.slug}`} />
          </div>
        </div>
      </section>

      {/* Related neighborhoods + internal links */}
      <section className="container-main py-12 sm:py-16">
        {/* Neighborhood articles cross-link block */}
        {hoodArticles.length > 0 && (
          <div className="mb-12">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">Read more</p>
                <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
                  Guides & resources for {cfg.name}
                </h2>
              </div>
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F2A44] hover:text-[#C8A96B]"
                    data-testid="hood-link-blog">
                Browse all articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {hoodArticles.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`}
                      className="group p-5 rounded-xl border border-[#D9D9D9] bg-white hover:border-[#C8A96B] hover:shadow-md transition-all"
                      data-testid={`hood-article-${a.slug}`}>
                  <p className="text-[10px] uppercase tracking-widest text-[#C8A96B] font-bold">
                    {a.type === "cornerstone" ? "Neighborhood Guide" : "Renter Resource"}
                  </p>
                  <h3 className="mt-1.5 text-base font-bold text-[#1F2A44] font-['Playfair_Display'] group-hover:text-[#C8A96B] transition-colors leading-snug">
                    {a.title}
                  </h3>
                  <p className="mt-2 text-xs text-[#333333]/70 line-clamp-2">{a.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">Explore more</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
              Related Houston Neighborhoods
            </h2>
          </div>
          <Link to="/apartment-search" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F2A44] hover:text-[#C8A96B]"
                data-testid="hood-back-to-search">
            See all Houston apartments <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {related.map((n) => (
            <Link key={n.slug} to={`/apartments/${n.slug}`}
                  className="group p-5 rounded-xl border border-[#D9D9D9] bg-white hover:border-[#C8A96B] hover:shadow-md transition-all"
                  data-testid={`related-${n.slug}`}>
              <p className="text-[10px] uppercase tracking-widest text-[#C8A96B] font-bold">{n.eyebrow}</p>
              <h3 className="mt-1.5 text-lg font-bold text-[#1F2A44] font-['Playfair_Display'] group-hover:text-[#C8A96B] transition-colors">
                {n.name}
              </h3>
              <p className="mt-2 text-xs text-[#333333]/70 line-clamp-3">{n.intro}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#C8A96B]">
                Browse listings <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>

        {/* Service cross-links */}
        <div className="mt-10 grid sm:grid-cols-3 gap-3 text-sm">
          <Link to="/relocation-services" className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#D9D9D9] hover:border-[#C8A96B] transition-colors"
                data-testid="hood-link-relocation">
            <Plane className="w-5 h-5 text-[#C8A96B]" />
            <span className="font-semibold text-[#1F2A44]">Relocation Services</span>
          </Link>
          <a href={TYPEFORM_QUIZ_URL} target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#D9D9D9] hover:border-[#C8A96B] transition-colors"
             data-testid="hood-link-quiz">
            <ClipboardList className="w-5 h-5 text-[#C8A96B]" />
            <span className="font-semibold text-[#1F2A44]">Take the Apartment Match Quiz</span>
          </a>
          <Link to="/luxury-apartments" className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#D9D9D9] hover:border-[#C8A96B] transition-colors"
                data-testid="hood-link-luxury">
            <Sparkles className="w-5 h-5 text-[#C8A96B]" />
            <span className="font-semibold text-[#1F2A44]">Luxury Houston Apartments</span>
          </Link>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1F2A44] text-white">
        <div className="container-main py-12 sm:py-16 text-center">
          <ShieldCheck className="w-8 h-8 text-[#C8A96B] mx-auto" />
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold font-['Playfair_Display']">
            Free {cfg.name} Apartment Locating
          </h2>
          <p className="mt-3 text-white/80 max-w-2xl mx-auto">
            Properties pay us. You pay nothing. Get a hand-picked shortlist from a {cfg.name} locator who knows the market block by block.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a href={BRAND.phoneHref}
               className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#C8A96B] hover:bg-[#B5915A] text-white font-bold"
               data-testid="hood-bottom-call">
              <Phone className="w-4 h-4" /> Call {BRAND.phone}
            </a>
            <a href={BRAND.emailHref}
               className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-white/30 hover:bg-white hover:text-[#1F2A44] font-semibold"
               data-testid="hood-bottom-email">
              Email us
            </a>
          </div>
          <p className="mt-6 text-xs text-white/50">
            {BRAND.name} · {BRAND.brokerageLine}
          </p>
        </div>
      </section>

      <StickyLeadModal
        source={`${cfg.leadSource}_sticky`}
        storageKey={`unq_sticky_${cfg.slug}_v1`}
      />
    </div>
  );
};

export default NeighborhoodPage;
